import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronUpDownIcon,
    ChevronUpIcon,
    DocumentArrowDownIcon,
    MagnifyingGlassIcon,
    PencilIcon,
} from "@heroicons/react/24/outline";

import classNames from "classnames";
import { t } from "i18next";
import * as React from "react";
import Button from "./Button";
import { FormSelect } from "./form/FormSelect";
import { SearchBar } from "./SearchBar";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    SortingState,
    ColumnFiltersState,
    VisibilityState,
} from "@tanstack/react-table";
import { SpinCircle } from "./SpinCircle";
import { set } from "react-hook-form";
import { CrossIcon } from "react-select/dist/declarations/src/components/indicators";

const paginationNumberOptions = [
    { label: "10", value: "10", key: "10" },
    { label: "25", value: "25", key: "25" },
    { label: "50", value: "50", key: "50" },
];

interface Props<TData> {
    data: TData[];
    columns: ColumnDef<TData, any>[];
    onRowClick?: (row: TData) => void;
    onExportClick?: (callback: (exporting: boolean) => void) => void;
    onEditingStarted?: () => void;
    onEditingFinished?: (canceled: boolean) => Promise<void> | void; 
    noPadding?: boolean;
    hideHeaders?: boolean;
    isSelectable?: boolean;
    tableHeader?: React.ReactElement;
    toolbar?: React.ReactElement;
    hasSearchBar?: boolean;
    title?: string;
    highlightRow?: (row: TData) => boolean;
    isEditable?: boolean;
}

export function CommissionsTable<TData>({
    data,
    columns,
    onRowClick,
    onExportClick,
    onEditingStarted,
    onEditingFinished,
    hideHeaders,
    tableHeader,
    toolbar,
    noPadding = false,
    hasSearchBar = true,
    title,
    highlightRow,
    isEditable = false,
}: Props<TData>) {
    const [globalFilter, setGlobalFilter] = React.useState("");
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: 10 });
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
    const [isExporting, setIsExporting] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            globalFilter,
            columnFilters,
            pagination,
            columnVisibility,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: "includesString",
        enableColumnFilters: true,
        debugTable: false,
    });

    return (
        <div
            className={classNames({
                "w-full grow flex flex-col items-end": true,
                "p-5": !noPadding,
            })}
        >
            {toolbar && <div className="flex flex-col w-full overflow-auto h-auto relative bg-white rounded-lg shadow mb-4">
                {toolbar}
            </div>}

            {tableHeader && <div className="flex flex-col w-full overflow-auto h-auto relative bg-white rounded-lg shadow mb-4">
                {tableHeader}
            </div>}
            
            <div
                className={classNames({
                    "flex flex-col w-full overflow-auto h-auto relative bg-white rounded-lg shadow":
                        true,
                })}
            >
                {(hasSearchBar || title) && (
                    <div className="w-full flex border-b">
                        <div
                            className={classNames({
                                "w-full flex gap-1 items-center": true,
                                "bg-white": true,
                            })}
                            >
                            <MagnifyingGlassIcon
                                width={30}
                                height={30}
                                className="text-gold ml-2 p-1"
                            />
                            <input
                                placeholder="Search"
                                type="text"
                                className={classNames({
                                "outline-none w-full h-full self-center m-1": true,
                                "bg-white": true,
                                })}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                value={globalFilter}
                            />
                        </div>

                        {isEditable && !isEditing && <Button
                            classNames="m-1"
                            isTerciary={true}
                            onClick={() => {
                                onEditingStarted && onEditingStarted();
                                setIsEditing(true);
                            }}
                            isDisabled={table.getRowModel().rows.length === 0}
                        >
                            <span className="flex items-center font-semibold pr-3">
                            <div className="w-8 p-1 aspect-square mr-2"><PencilIcon/></div>
                            {t("EDIT")}
                            </span>
                        </Button>}

                        {isEditable && isEditing && <Button
                            classNames="m-1"
                            isTerciary={true}
                            onClick={() => {
                                onEditingFinished && onEditingFinished(true);
                                setIsEditing(false);
                            }}
                            isDisabled={table.getRowModel().rows.length === 0}
                        >
                            <span className="flex items-center font-semibold p-1">
                            
                            {t("CANCEL")}
                            </span>
                        </Button>}

                        {isEditable && isEditing && <Button
                            classNames="m-1"
                            onClick={async () => {
                                if (onEditingFinished) {
                                    try {
                                        await onEditingFinished(false);
                                        setIsEditing(false);
                                    } catch (_) {}
                                    
                                } else {
                                    setIsEditing(false);
                                }
                                
                            }}
                            isDisabled={table.getRowModel().rows.length === 0}
                        >
                            <span className="flex items-center font-semibold p-1">
                            
                            {t("SAVE")}
                            </span>
                        </Button>}

                        {onExportClick && !isEditing && <Button
                            classNames="m-1"
                            isTerciary={true}
                            onClick={() => onExportClick(exporting => setIsExporting(exporting))}
                            isDisabled={table.getRowModel().rows.length === 0 || isExporting}
                        >
                            <span className="flex items-center font-semibold pr-3">
                            <div className="w-8 p-1 aspect-square mr-2">
                                {isExporting ? <SpinCircle/> : <DocumentArrowDownIcon /> }
                            </div>
                            {t("EXPORT")}
                            </span>
                        </Button>}
                    </div>
                )}
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-gray-700">
                        {!hideHeaders && table.getHeaderGroups().map((headerGroup) => (
                            <><tr
                                key={headerGroup.id}
                                className={classNames({
                                    "text-blue sticky top-0 bg-white border-gray-200": true,
                                    "border-b" : !headerGroup.headers.some((header) => header.column.getCanFilter() && header.column.columnDef.enableColumnFilter !== false),
                                })}
                            >
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        className="px-3 py-4"
                                        {...{
                                            onClick: header.column.getCanSort()
                                                ? header.column.getToggleSortingHandler()
                                                : undefined,
                                            style: { cursor: header.column.getCanSort() ? "pointer" : undefined },
                                        }}
                                    >
                                        <div className="flex text-md font-medium items-start gap-1">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.getCanSort() && (
                                                    <span>
                                                        {{
                                                            asc: <ChevronUpIcon className="w-4 h-4 ml-1 mt-1" />,
                                                            desc: <ChevronDownIcon className="w-4 h-4 ml-1 mt-1" />,
                                                        }[header.column.getIsSorted() as string] ?? <ChevronUpDownIcon className="w-4 h-4 ml-1 mt-1" />}
                                                    </span>
                                                )}
                                            </div>
                                        
                                    </th>
                                ))}
                            </tr>

                            {headerGroup.headers.some((header) => header.column.getCanFilter() && header.column.columnDef.enableColumnFilter !== false) &&
                            <tr
                                key={headerGroup.id+'filter'}
                                className="text-blue sticky top-0 bg-white border-b border-t-0 border-gray-200"
                            >
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        className="p-3 pt-0"
                                    >
                                        {header.column.getCanFilter() && header.column.columnDef.enableColumnFilter !== false && (
                                            <div className="flex">
                                                {header.column.getFilterFn() && (
                                                    <input
                                                        type="text"
                                                        value={(header.column.getFilterValue() ?? "") as string}
                                                        onChange={e => header.column.setFilterValue(e.target.value)}
                                                        placeholder={t("FILTER") as string}
                                                        className="border rounded px-2 py-1 text-xs grow"
                                                        style={{ width: 80 }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    
                                    </th>
                                ))}
                            </tr>}</>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            const isHighlighted = highlightRow ? highlightRow(row.original) : false;
                            return (
                                <tr
                                    key={row.id}
                                    className={classNames({
                                        "bg-white hover:bg-neutral-100 cursor-pointer": !isHighlighted,
                                        "bg-gold-100 hover:bg-gold-200 cursor-pointer": isHighlighted,
                                    })}
                                    onClick={() => onRowClick?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="p-3 align-top">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="flex justify-end items-center text-xs text-gray-700 p-3 gap-6">
                    <div className="flex items-center">
                        <p>{t("ITEMS_PER_PAGE")}:</p>
                        <FormSelect
                            width="w-28"
                            name="numberItems"
                            label=""
                            options={paginationNumberOptions}
                            selectedValue={String(table.getState().pagination.pageSize)}
                            handleOnChange={(e) =>
                                table.setPageSize(Number(e.target.value))
                            }
                        />
                    </div>
                    <p>
                        {data.length === 0
                            ? "0"
                            : table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
                        {" - "}
                        {Math.min(
                            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                            table.getFilteredRowModel().rows.length
                        )}{" "}
                        {t("OF")} {table.getFilteredRowModel().rows.length}
                    </p>
                    <div className="flex items-center">
                        <Button
                            isTerciary
                            isDisabled={!table.getCanPreviousPage()}
                            onClick={() => table.previousPage()}
                            iconOnly
                            classNames="w-9 p-1 aspect-square mr-4"
                        >
                            <ChevronLeftIcon />
                        </Button>
                        <Button
                            isTerciary
                            isDisabled={!table.getCanNextPage()}
                            onClick={() => table.nextPage()}
                            iconOnly
                            classNames="w-9 p-1 aspect-square mr-4"
                        >
                            <ChevronRightIcon />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CommissionsTable;